import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Calendar,
  Clock,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Users,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Search,
  Filter,
} from "lucide-react";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import { useAuth } from "../../hooks/useAuth";
import {
  getClasses,
  addPeriodToSchedule,
  deletePeriodFromSchedule,
} from "../../services/classes";
import { getCourses } from "../../services/courses";
import { getTeachers } from "../../services/teachers";

// Types
interface SchedulePeriod {
  _id?: string;
  day:
    | "monday"
    | "tuesday"
    | "wednesday"
    | "thursday"
    | "friday"
    | "saturday"
    | "sunday";
  startTime: string;
  endTime: string;
  subjectName: string;
  teacherId?: string;
  room?: string;
}

interface ClassSchedule {
  _id: string;
  name: string;
  grade: string;
  section: string;
  schedule?: {
    periods: SchedulePeriod[];
  };
}

const DAYS_OF_WEEK: SchedulePeriod["day"][] = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const TIME_SLOTS = [
  "08:00",
  "08:30",
  "09:00",
  "09:30",
  "10:00",
  "10:30",
  "11:00",
  "11:30",
  "12:00",
  "12:30",
  "13:00",
  "13:30",
  "14:00",
  "14:30",
  "15:00",
  "15:30",
  "16:00",
  "16:30",
  "17:00",
  "17:30",
  "18:00",
];

// Helper function to check if a time slot is within a period's duration
const isTimeSlotInPeriod = (
  timeSlot: string,
  period: SchedulePeriod
): boolean => {
  const slotTime = timeSlot.replace(":", "");
  const startTime = period.startTime.replace(":", "");
  const endTime = period.endTime.replace(":", "");

  const slot = parseInt(slotTime);
  const start = parseInt(startTime);
  const end = parseInt(endTime);

  return slot >= start && slot < end;
};

// Helper function to check if a time slot is the start of a period
const isTimeSlotStart = (timeSlot: string, period: SchedulePeriod): boolean => {
  return period.startTime === timeSlot;
};

// Helper function to calculate period span (how many slots it covers)
const calculatePeriodSpan = (period: SchedulePeriod): number => {
  const startTime = period.startTime.replace(":", "");
  const endTime = period.endTime.replace(":", "");

  const start = parseInt(startTime);
  const end = parseInt(endTime);

  // Calculate duration in minutes
  const startMinutes = Math.floor(start / 100) * 60 + (start % 100);
  const endMinutes = Math.floor(end / 100) * 60 + (end % 100);
  const durationMinutes = endMinutes - startMinutes;

  // Each slot is 30 minutes, so divide by 30
  return Math.ceil(durationMinutes / 30);
};

const SecretarySchedule: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // State
  const [selectedClass, setSelectedClass] = useState<string>("");
  const [editingPeriod, setEditingPeriod] = useState<SchedulePeriod | null>(
    null
  );
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDay, setSelectedDay] = useState<string>("all");

  // Form state for new/edit period
  const [periodForm, setPeriodForm] = useState<Partial<SchedulePeriod>>({
    day: "monday",
    startTime: "09:00",
    endTime: "10:00",
    subjectName: "",
    teacherId: "",
    room: "",
  });

  // Fetch classes
  const {
    data: classesData,
    isLoading: classesLoading,
    error: classesError,
  } = useQuery({
    queryKey: ["classes"],
    queryFn: () => getClasses({ page: 1, limit: 100 }),
  });

  // Fetch courses
  const { data: coursesData, isLoading: coursesLoading } = useQuery({
    queryKey: ["courses"],
    queryFn: () => getCourses({ page: 1, limit: 100 }),
  });

  // Fetch teachers
  const { data: teachersData, isLoading: teachersLoading } = useQuery({
    queryKey: ["teachers"],
    queryFn: () => getTeachers({ page: 1, limit: 100 }),
  });

  const classes = classesData?.classes || [];
  const courses = coursesData?.data || [];
  const teachers = teachersData?.data || [];

  // Get selected class details
  const currentClass = classes.find((cls: any) => cls._id === selectedClass);
  const periods: SchedulePeriod[] =
    (currentClass?.schedule as any)?.periods || [];

  // Filter periods by day
  const filteredPeriods =
    selectedDay === "all"
      ? periods
      : periods.filter((period: SchedulePeriod) => period.day === selectedDay);

  // Group periods by day for calendar view
  const periodsByDay = DAYS_OF_WEEK.reduce((acc, day) => {
    acc[day] = periods
      .filter((period: SchedulePeriod) => period.day === day)
      .sort((a, b) => a.startTime.localeCompare(b.startTime));
    return acc;
  }, {} as Record<string, SchedulePeriod[]>);

  // Add period mutation
  const addPeriodMutation = useMutation({
    mutationFn: async (data: SchedulePeriod) => {
      if (!selectedClass) throw new Error("No class selected");
      return addPeriodToSchedule(selectedClass, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["classes"] });
      setShowAddModal(false);
      resetForm();
    },
    onError: (error) => {
      console.error("Error adding period:", error);
      // You could add a toast notification here
    },
  });

  // Delete period mutation
  const deletePeriodMutation = useMutation({
    mutationFn: async ({
      classId,
      periodIndex,
    }: {
      classId: string;
      periodIndex: number;
    }) => {
      return deletePeriodFromSchedule(classId, periodIndex);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["classes"] });
    },
    onError: (error) => {
      console.error("Error deleting period:", error);
    },
  });

  // Reset form
  const resetForm = () => {
    setPeriodForm({
      day: "monday" as const,
      startTime: "09:00",
      endTime: "10:00",
      subjectName: "",
      teacherId: "",
      room: "",
    });
    setEditingPeriod(null);
  };

  // Handle delete period
  const handleDeletePeriod = (periodIndex: number) => {
    if (!selectedClass) return;

    if (window.confirm("Are you sure you want to delete this period?")) {
      deletePeriodMutation.mutate({
        classId: selectedClass,
        periodIndex,
      });
    }
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (
      !selectedClass ||
      !periodForm.subjectName ||
      !periodForm.startTime ||
      !periodForm.endTime
    )
      return;

    addPeriodMutation.mutate(periodForm as SchedulePeriod);
  };

  // Get teacher name
  const getTeacherName = (teacherId?: string) => {
    if (!teacherId) return "Not assigned";
    const teacher = teachers.find((t: any) => t._id === teacherId);
    return teacher
      ? `${teacher.userInfo?.firstName || ""} ${
          teacher.userInfo?.lastName || ""
        }`.trim()
      : "Unknown";
  };

  // Get subject color with gradient
  const getSubjectColor = (subject: string) => {
    const colors = [
      "bg-gradient-to-br from-blue-300 to-indigo-400 text-white font-bold shadow-sm",
      "bg-gradient-to-br from-emerald-300 to-emerald-400 text-white font-bold shadow-sm",
      "bg-gradient-to-br from-purple-300 to-purple-400 text-white font-bold shadow-sm",
      "bg-gradient-to-br from-amber-300 to-yellow-400 text-white font-bold shadow-sm",
      "bg-gradient-to-br from-pink-300 to-pink-400 text-white font-bold shadow-sm",
      "bg-gradient-to-br from-indigo-300 to-indigo-400 text-white font-bold shadow-sm",
    ];
    return colors[subject.length % colors.length];
  };

  if (classesLoading || coursesLoading || teachersLoading) {
    return (
      <div className="flex items-center justify-center h-64 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-slate-200 border-t-blue-500"></div>
          <p className="mt-4 text-slate-600 font-medium">
            Loading schedules...
          </p>
        </div>
      </div>
    );
  }

  if (classesError) {
    return (
      <div className="p-6 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 min-h-screen">
        <div className="bg-white/95 backdrop-blur-xl border border-red-200/60 rounded-2xl p-6 shadow-lg">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-300 to-pink-400 flex items-center justify-center shadow-md">
              <X className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-red-800 font-bold">Error Loading Data</h3>
              <p className="text-red-600 mt-1 font-medium">
                Unable to load classes data. Please try refreshing the page.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 lg:p-6 max-w-7xl mx-auto bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 min-h-screen">
      {/* Header */}
      <div className="mb-6 lg:mb-8 bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg p-4 lg:p-6 border border-slate-200/60">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
          Class Scheduling
        </h1>
        <p className="text-slate-600 mt-2 text-sm sm:text-base font-medium">
          Manage class schedules and time tables
        </p>
      </div>

      {/* Controls */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 lg:mb-6">
        {/* Class Selection */}
        <div>
          <label className="block text-sm font-bold text-slate-700 mb-2">
            Select Class
          </label>
          <select
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300/60 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm shadow-sm transition-all duration-300 font-semibold text-slate-700 bg-white"
          >
            <option value="">Choose a class...</option>
            {classes.map((cls: any) => (
              <option key={cls._id} value={cls._id}>
                {cls.name} - {cls.section}
              </option>
            ))}
          </select>
        </div>

        {/* Day Filter */}
        <div>
          <label className="block text-sm font-bold text-slate-700 mb-2">
            Filter by Day
          </label>
          <select
            value={selectedDay}
            onChange={(e) => setSelectedDay(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300/60 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm shadow-sm transition-all duration-300 font-semibold text-slate-700 bg-white"
          >
            <option value="all">All Days</option>
            {DAYS_OF_WEEK.map((day) => (
              <option key={day} value={day}>
                {day.charAt(0).toUpperCase() + day.slice(1)}
              </option>
            ))}
          </select>
        </div>

        {/* Add Period Button */}
        {/* Add Period Button */}
        <div className="sm:col-span-2 lg:col-span-2 flex items-end">
          <button
            onClick={() => {
              resetForm();
              setShowAddModal(true);
            }}
            disabled={!selectedClass}
            className={`group relative px-6 py-2.5 rounded-xl font-bold text-white shadow-lg transition-all duration-300 w-full sm:w-auto ${
              !selectedClass
                ? "bg-slate-300 cursor-not-allowed"
                : "bg-gradient-to-r from-blue-300 to-blue-400 hover:shadow-xl hover:scale-105"
            }`}
          >
            <Plus className="w-5 h-5 mr-2 inline-block" />
            Add Period
          </button>
        </div>
      </div>

      {selectedClass ? (
        <div className="space-y-6">
          {/* Class Info */}
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg border border-slate-200/60 p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-900">
                  {currentClass?.name} - {currentClass?.section}
                </h2>
                <p className="text-slate-600 font-semibold mt-1">
                  Grade: {currentClass?.grade} | Total Periods: {periods.length}
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-300 to-purple-400 flex items-center justify-center shadow-md">
                  <Users className="h-5 w-5 text-white" />
                </div>
                <span className="text-sm font-bold text-slate-700">
                  {currentClass?.students?.length || 0} Students
                </span>
              </div>
            </div>
          </div>

          {/* Weekly Schedule Calendar */}
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg border border-slate-200/60 overflow-hidden">
            <div className="p-3 sm:p-4 border-b border-slate-200/60 bg-gradient-to-r from-slate-50 to-blue-50">
              <h3 className="text-lg font-bold text-slate-900">
                Weekly Schedule
              </h3>
              <p className="text-sm text-slate-600 mt-1 font-medium">
                Scroll horizontally to view all days
              </p>
            </div>

            <div className="overflow-x-auto">
              <div className="grid grid-cols-8 min-w-[800px]">
                {/* Ensures minimum width for better readability */}
                {/* Time column header */}
                <div className="p-2 sm:p-3 border-r border-slate-200/60 bg-gradient-to-r from-slate-50 to-blue-50 font-bold text-xs sm:text-sm text-slate-700">
                  Time
                </div>

                {/* Day headers */}
                {DAYS_OF_WEEK.map((day) => (
                  <div
                    key={day}
                    className="p-2 sm:p-3 border-r border-slate-200/60 bg-gradient-to-r from-slate-50 to-blue-50 font-bold text-xs sm:text-sm text-slate-700 text-center"
                  >
                    {day.charAt(0).toUpperCase() + day.slice(1)}
                  </div>
                ))}

                {/* Time slots and periods */}
                {TIME_SLOTS.map((timeSlot) => (
                  <React.Fragment key={timeSlot}>
                    {/* Time slot */}
                    <div className="p-1 sm:p-2 border-r border-b border-slate-200/60 bg-slate-50/50 text-xs text-slate-600 font-semibold">
                      {timeSlot}
                    </div>

                    {/* Day cells */}
                    {DAYS_OF_WEEK.map((day) => {
                      const dayPeriods = periodsByDay[day] || [];

                      // Find period that starts at this time slot
                      const startingPeriod = dayPeriods.find(
                        (p) => p.startTime === timeSlot
                      );

                      // Find period that spans this time slot (but doesn't start here)
                      const spanningPeriod = dayPeriods.find(
                        (p) =>
                          isTimeSlotInPeriod(timeSlot, p) &&
                          p.startTime !== timeSlot
                      );

                      return (
                        <div
                          key={`${day}-${timeSlot}`}
                          className="p-1 border-r border-b border-slate-200/60 min-h-[50px] relative hover:bg-blue-50/30 transition-colors"
                        >
                          {startingPeriod && (
                            <div
                              className={`p-2 rounded-lg text-xs ${getSubjectColor(
                                startingPeriod.subjectName
                              )}`}
                              style={{
                                height: `${
                                  calculatePeriodSpan(startingPeriod) * 50 - 8
                                }px`,
                                zIndex: 10,
                                position: "absolute",
                                top: "4px",
                                left: "4px",
                                right: "4px",
                              }}
                            >
                              <div className="font-bold truncate">
                                {startingPeriod.subjectName}
                              </div>
                              <div className="text-xs opacity-90">
                                {startingPeriod.startTime} -{" "}
                                {startingPeriod.endTime}
                              </div>
                              {startingPeriod.room && (
                                <div className="text-xs opacity-90">
                                  {startingPeriod.room}
                                </div>
                              )}
                              <div className="mt-1 flex gap-1">
                                <button
                                  onClick={() =>
                                    setEditingPeriod(startingPeriod)
                                  }
                                  className="text-xs bg-white/30 hover:bg-white/50 rounded-lg px-2 py-1 transition-all hover:scale-105"
                                >
                                  <Edit className="h-3 w-3" />
                                </button>
                                <button
                                  onClick={() => {
                                    const periodIndex = periods.findIndex(
                                      (p) =>
                                        p.startTime ===
                                          startingPeriod.startTime &&
                                        p.day === startingPeriod.day &&
                                        p.subjectName ===
                                          startingPeriod.subjectName
                                    );
                                    if (periodIndex !== -1) {
                                      handleDeletePeriod(periodIndex);
                                    }
                                  }}
                                  className="text-xs bg-white/30 hover:bg-white/50 rounded-lg px-2 py-1 transition-all hover:scale-105"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              </div>
                            </div>
                          )}
                          {spanningPeriod && !startingPeriod && (
                            <div
                              className={`p-1 rounded-lg text-xs opacity-60 ${getSubjectColor(
                                spanningPeriod.subjectName
                              )}`}
                              style={{
                                position: "absolute",
                                top: "4px",
                                left: "4px",
                                right: "4px",
                                bottom: "4px",
                                zIndex: 5,
                              }}
                            >
                              {/* This shows the period continues from previous slot */}
                              <div className="text-center text-xs opacity-90">
                                ↑ {spanningPeriod.subjectName}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>

          {/* Periods List */}
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg border border-slate-200/60">
            <div className="p-4 border-b border-slate-200/60 bg-gradient-to-r from-slate-50 to-blue-50">
              <h3 className="text-lg font-bold text-slate-900">
                Schedule Periods
              </h3>
            </div>

            <div className="p-4">
              {filteredPeriods.length > 0 ? (
                <div className="space-y-3">
                  {filteredPeriods.map(
                    (period: SchedulePeriod, index: number) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-4 border border-slate-200/60 rounded-xl hover:shadow-md hover:scale-[1.02] transition-all duration-300 bg-white"
                      >
                        <div className="flex items-center space-x-4">
                          <div
                            className={`px-3 py-1 rounded-xl text-sm ${getSubjectColor(
                              period.subjectName
                            )}`}
                          >
                            {period.day.charAt(0).toUpperCase() +
                              period.day.slice(1)}
                          </div>
                          <div>
                            <h4 className="font-bold text-slate-900">
                              {period.subjectName}
                            </h4>
                            <p className="text-sm text-slate-600 font-semibold">
                              {period.startTime} - {period.endTime}
                              {period.room && ` | Room: ${period.room}`}
                            </p>
                            <p className="text-xs text-slate-500 font-medium">
                              Teacher: {getTeacherName(period.teacherId)}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => {
                              setEditingPeriod(period);
                              setPeriodForm(period);
                              setShowAddModal(true);
                            }}
                            className="p-2 rounded-lg bg-gradient-to-br from-blue-300 to-blue-400 text-white hover:shadow-md hover:scale-110 transition-all duration-300"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              handleDeletePeriod(index);
                            }}
                            className="p-2 rounded-lg bg-gradient-to-br from-red-300 to-pink-400 text-white hover:shadow-md hover:scale-110 transition-all duration-300"
                            disabled={deletePeriodMutation.isPending}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    )
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-300 to-emerald-400 flex items-center justify-center mx-auto mb-4 shadow-md">
                    <Calendar className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">
                    No periods scheduled
                  </h3>
                  <p className="text-slate-600 mb-4 font-medium">
                    {selectedDay === "all"
                      ? "This class doesn't have any periods scheduled yet."
                      : `No periods scheduled for ${selectedDay}.`}
                  </p>
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="px-6 py-2.5 bg-gradient-to-r from-blue-300 to-blue-400 text-white font-bold rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300"
                  >
                    Add First Period
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg border border-slate-200/60 p-8">
          <div className="text-center">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-300 to-indigo-400 flex items-center justify-center mx-auto mb-4 shadow-md">
              <BookOpen className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">
              Select a Class
            </h3>
            <p className="text-slate-600 font-medium">
              Choose a class from the dropdown above to view and manage its
              schedule.
            </p>
          </div>
        </div>
      )}

      {/* Add/Edit Period Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-slate-900/75 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl w-full max-w-md border border-slate-200/60">
            <div className="flex items-center justify-between p-4 border-b border-slate-200/60 bg-gradient-to-r from-slate-50 to-blue-50">
              <h3 className="text-lg font-bold text-slate-900">
                {editingPeriod ? "Edit Period" : "Add New Period"}
              </h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
                className="p-2 hover:bg-slate-200/50 rounded-xl transition-all duration-300 hover:scale-110"
              >
                <X className="h-5 w-5 text-slate-500" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              {/* Day */}
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  Day
                </label>
                <select
                  value={periodForm.day}
                  onChange={(e) =>
                    setPeriodForm({
                      ...periodForm,
                      day: e.target.value as SchedulePeriod["day"],
                    })
                  }
                  className="w-full px-3 py-2 border border-slate-300/60 rounded-xl focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 text-slate-700 font-semibold shadow-sm transition-all"
                  required
                >
                  {DAYS_OF_WEEK.map((day) => (
                    <option key={day} value={day}>
                      {day.charAt(0).toUpperCase() + day.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">
                    Start Time
                  </label>
                  <select
                    value={periodForm.startTime}
                    onChange={(e) =>
                      setPeriodForm({
                        ...periodForm,
                        startTime: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-slate-300/60 rounded-xl focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 text-slate-700 font-semibold shadow-sm transition-all"
                    required
                  >
                    {TIME_SLOTS.map((time) => (
                      <option key={time} value={time}>
                        {time}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">
                    End Time
                  </label>
                  <select
                    value={periodForm.endTime}
                    onChange={(e) =>
                      setPeriodForm({
                        ...periodForm,
                        endTime: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-slate-300/60 rounded-xl focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 text-slate-700 font-semibold shadow-sm transition-all"
                    required
                  >
                    {TIME_SLOTS.map((time) => (
                      <option key={time} value={time}>
                        {time}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Subject */}
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  Subject/Course
                </label>
                <select
                  value={periodForm.subjectName}
                  onChange={(e) =>
                    setPeriodForm({
                      ...periodForm,
                      subjectName: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-slate-300/60 rounded-xl focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 text-slate-700 font-semibold shadow-sm transition-all"
                  required
                >
                  <option value="">Select a course...</option>
                  {courses.map((course: any) => (
                    <option key={course._id} value={course.name}>
                      {course.name}({course.level})
                    </option>
                  ))}
                </select>
              </div>

              {/* Teacher */}
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  Teacher
                </label>
                <select
                  value={periodForm.teacherId}
                  onChange={(e) =>
                    setPeriodForm({
                      ...periodForm,
                      teacherId: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-slate-300/60 rounded-xl focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 text-slate-700 font-semibold shadow-sm transition-all"
                >
                  <option value="">Select a teacher...</option>
                  {teachers.map((teacher: any) => (
                    <option key={teacher._id} value={teacher._id}>
                      {teacher.userInfo?.firstName} {teacher.userInfo?.lastName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Room */}
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  Room (Optional)
                </label>
                <input
                  type="text"
                  value={periodForm.room || ""}
                  onChange={(e) =>
                    setPeriodForm({ ...periodForm, room: e.target.value })
                  }
                  placeholder="e.g., Room 101, Lab A"
                  className="w-full px-3 py-2 border border-slate-300/60 rounded-xl focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 text-slate-700 font-semibold shadow-sm transition-all placeholder:text-slate-400"
                />
              </div>

              {/* Submit Button */}
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 font-bold rounded-xl hover:bg-slate-50 transition-all duration-300 hover:scale-105 shadow-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addPeriodMutation.isPending}
                  className="flex-1 px-4 py-2.5 bg-gradient-to-r from-emerald-300 to-emerald-400 text-white font-bold rounded-xl hover:shadow-xl hover:scale-105 disabled:from-slate-300 disabled:to-slate-300 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center space-x-2"
                >
                  {addPeriodMutation.isPending ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      <span>{editingPeriod ? "Update" : "Add"} Period</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecretarySchedule;
